import React, { useState } from 'react';
import { getTextSize, getTextSizes, setTextSize } from '../utils/textSize';
import usePWAInstall from '../hooks/usePWAInstall';
import IOSInstallGuide from './IOSInstallGuide';

const Settings = () => {
  const [currentSize, setCurrentSize] = useState(getTextSize());
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const { isIOS, isInstallable, isStandalone, triggerInstall } = usePWAInstall();
  const sizes = getTextSizes();

  const handleSizeChange = (key) => {
    setTextSize(key);
    setCurrentSize(key);
  };

  const handlePWAAction = () => {
    if (isIOS) {
      setShowIOSGuide(true);
    } else if (isInstallable) {
      triggerInstall();
    } else {
      setShowIOSGuide(true); // Mostrar la guía PWA general / iOS
    }
  };

  return (
    <div style={{ padding: '2rem 1rem', color: 'var(--text-primary)', height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <h1 style={{ color: 'var(--gold-matte)', fontFamily: "var(--font-tactical)", fontSize: '1.2rem', letterSpacing: '3px', textTransform: 'uppercase', margin: 0 }}>
        ⚙️ Configuración
      </h1>

      {/* Sección 1: Tamaño de Texto */}
      <div style={{
        background: 'rgba(10, 26, 42, 0.75)',
        border: '1px solid var(--tech-border)',
        borderTop: '2px solid var(--gold-matte)',
        borderRadius: '8px',
        padding: '1.5rem',
      }}>
        <h3 style={{ color: 'var(--gold-matte)', fontFamily: "var(--font-tactical)", fontSize: '0.8rem', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '1.2rem' }}>
          Tamaño del texto
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.2rem', lineHeight: '1.5' }}>
          Ajusta el tamaño del texto para mejorar la legibilidad en cuestionarios y simulador.
        </p>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {Object.entries(sizes).map(([key, config]) => (
            <button
              key={key}
              onClick={() => handleSizeChange(key)}
              style={{
                flex: 1,
                minWidth: '110px',
                padding: '1rem',
                background: currentSize === key
                  ? 'rgba(197,160,89,0.15)'
                  : 'rgba(197,160,89,0.06)',
                border: currentSize === key
                  ? '2px solid var(--gold-matte)'
                  : '1px solid var(--tech-border)',
                borderLeft: currentSize === key ? '3px solid var(--gold-matte)' : '1px solid var(--tech-border)',
                borderRadius: '6px',
                color: currentSize === key ? 'var(--gold-light)' : 'var(--text-primary)',
                cursor: 'pointer',
                transition: 'all 0.25s',
                fontFamily: "var(--font-tactical)",
                fontSize: key === 'small' ? '0.65rem' : key === 'large' ? '0.85rem' : '0.75rem',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: key === 'small' ? '0.8rem' : key === 'large' ? '1.4rem' : '1.1rem', marginBottom: '0.5rem' }}>
                A
              </div>
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sección 2: Aplicación Web Progresiva / iPhone */}
      <div style={{
        background: 'rgba(10, 26, 42, 0.75)',
        border: '1px solid var(--tech-border)',
        borderTop: '2px solid #c5a059',
        borderRadius: '8px',
        padding: '1.5rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
          <h3 style={{ color: '#c5a059', fontFamily: "var(--font-tactical)", fontSize: '0.8rem', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>
            📲 Instalación en Dispositivos (PWA)
          </h3>
          <span style={{
            fontSize: '0.7rem',
            padding: '4px 10px',
            borderRadius: '12px',
            background: isStandalone ? 'rgba(16, 185, 129, 0.2)' : 'rgba(197, 160, 89, 0.2)',
            color: isStandalone ? '#34d399' : '#c5a059',
            border: `1px solid ${isStandalone ? '#10b981' : '#c5a059'}`,
            fontWeight: 'bold',
          }}>
            {isStandalone ? '🟢 Modo App Nativo' : '🌐 Ejecutando en Navegador'}
          </span>
        </div>

        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.2rem', lineHeight: '1.5' }}>
          Vortex EXANI-II está optimizada como una Aplicación Web Progresiva (PWA). Puedes agregarla a tu pantalla de inicio en <strong>iPhone (iOS)</strong>, Android o PC para usarla a pantalla completa sin barras de navegador.
        </p>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handlePWAAction}
            style={{
              background: 'linear-gradient(135deg, #c5a059 0%, #a88440 100%)',
              border: 'none',
              borderRadius: '6px',
              color: '#071526',
              fontFamily: "var(--font-tactical)",
              fontSize: '0.75rem',
              fontWeight: 800,
              padding: '12px 20px',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(197, 160, 89, 0.3)',
            }}
          >
            <span>📱</span>
            <span>{isIOS ? 'Ver Guía de Instalación para iPhone' : 'Instalar / Ver Guía de App'}</span>
          </button>
        </div>
      </div>

      {showIOSGuide && <IOSInstallGuide onClose={() => setShowIOSGuide(false)} />}
    </div>
  );
};

export default Settings;

